import type {
  NyxClient,
  NyxEvent,
  NyxMode,
  NyxQuestion,
  NyxTask,
} from '@atum-remote/nyx-link-protocol';
import type { Tone } from './format.js';

/**
 * Presentation rules for the AI work tab. Everything here is pure so the
 * transcript shape, task grouping and labels can be tested without a host.
 */

export type NyxTaskState = NyxTask['state'];
export type NyxApprovalAction = Extract<NyxEvent, { kind: 'approval' }>['action'];

export const clientLabel: Readonly<Record<NyxClient, string>> = {
  codex: 'Codex',
  claude: 'Claude Code',
  cursor: 'Cursor Agent',
};

/** Short form for dense rows and speaker labels. */
export const clientShortLabel: Readonly<Record<NyxClient, string>> = {
  codex: 'Codex',
  claude: 'Claude',
  cursor: 'Cursor',
};

export const clientDescription: Readonly<Record<NyxClient, string>> = {
  codex: 'OpenAI Codex CLI',
  claude: 'Anthropic Claude Code CLI',
  cursor: 'Cursor Agent CLI',
};

export const modeLabel: Readonly<Record<NyxMode, string>> = {
  ask_me: 'Ask me',
  plan_only: 'Plan only',
  auto_approve: 'Auto-approve',
};

export const modeDescription: Readonly<Record<NyxMode, string>> = {
  ask_me: 'The client stops and asks before commands, file changes and permissions.',
  plan_only: 'The client reads and plans without changing anything.',
  auto_approve: 'The client acts without asking, using your installed client’s own permissions.',
};

export const stateLabel: Readonly<Record<NyxTaskState, string>> = {
  idle: 'Ready',
  running: 'Working',
  waiting: 'Needs you',
  ended: 'Ended',
  interrupted: 'Interrupted',
  failed: 'Failed',
};

export const stateTone: Readonly<Record<NyxTaskState, Tone>> = {
  idle: 'ok',
  running: 'live',
  waiting: 'warn',
  ended: 'muted',
  interrupted: 'muted',
  failed: 'danger',
};

export const approvalLabel: Readonly<Record<NyxApprovalAction, string>> = {
  command: 'Run a command',
  file_change: 'Change files',
  permission: 'Grant a permission',
  other: 'Approve an action',
};

export function taskEnded(task: NyxTask | null | undefined): boolean {
  return !task || task.state === 'ended' || task.state === 'failed' || task.state === 'interrupted';
}

/** Task timestamps are epoch values; accept seconds as well as milliseconds. */
export function taskTimeIso(value: number): string | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  const ms = value < 1e12 ? value * 1000 : value;
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export interface TaskGroups {
  readonly attention: NyxTask[];
  readonly active: NyxTask[];
  readonly finished: NyxTask[];
}

/** Tasks that need a person first, then live work, then history; newest first within each. */
export function groupTasks(tasks: readonly NyxTask[]): TaskGroups {
  const byRecent = (a: NyxTask, b: NyxTask) => b.updatedAt - a.updatedAt;
  const attention = tasks.filter((task) => task.state === 'waiting').sort(byRecent);
  const active = tasks
    .filter((task) => task.state === 'running' || task.state === 'idle')
    .sort(byRecent);
  const finished = tasks.filter((task) => taskEnded(task)).sort(byRecent);
  return { attention, active, finished };
}

export interface ProjectGroup {
  readonly projectKey: string;
  readonly name: string;
  /** Newest activity first within the project. */
  readonly tasks: NyxTask[];
  readonly waiting: number;
  readonly active: number;
  readonly updatedAt: number;
}

/**
 * The sidebar groups tasks by the repository they run in, the way Codex and
 * Claude Code list threads under a directory. Projects with a task that needs
 * a person come first, then the most recently active ones.
 */
export function groupTasksByProject(
  tasks: readonly NyxTask[],
  names: Readonly<Record<string, string>> = {},
): ProjectGroup[] {
  const groups = new Map<string, NyxTask[]>();
  for (const task of tasks) {
    const list = groups.get(task.projectKey);
    if (list) list.push(task);
    else groups.set(task.projectKey, [task]);
  }
  return [...groups]
    .map(([projectKey, list]): ProjectGroup => {
      const sorted = [...list].sort((a, b) => b.updatedAt - a.updatedAt);
      return {
        projectKey,
        name: names[projectKey] ?? projectKey,
        tasks: sorted,
        waiting: sorted.filter((task) => task.state === 'waiting').length,
        active: sorted.filter((task) => task.state === 'running' || task.state === 'idle').length,
        updatedAt: sorted[0]?.updatedAt ?? 0,
      };
    })
    .sort((a, b) => Number(b.waiting > 0) - Number(a.waiting > 0) || b.updatedAt - a.updatedAt);
}

export type ToolStatus = 'pending' | 'done' | 'failed';

export interface ToolRow {
  readonly key: number;
  readonly name: string;
  readonly detail: string;
  readonly result: string | null;
  readonly status: ToolStatus;
}

export type TranscriptItem =
  | {
      readonly kind: 'message';
      readonly key: number;
      readonly speaker: 'user' | 'agent';
      text: string;
    }
  | { readonly kind: 'activity'; readonly key: number; readonly tools: ToolRow[] }
  | {
      readonly kind: 'approval';
      readonly key: number;
      readonly requestId: string;
      readonly action: NyxApprovalAction;
      readonly summary: string;
      readonly pending: boolean;
      readonly decision: boolean | null;
    }
  | {
      readonly kind: 'question';
      readonly key: number;
      readonly requestId: string;
      readonly questions: NyxQuestion[];
      readonly pending: boolean;
      readonly answered: boolean;
    }
  | {
      readonly kind: 'notice';
      readonly key: number;
      readonly tone: 'muted' | 'warn' | 'danger';
      readonly text: string;
    };

export interface TranscriptInput {
  readonly client: NyxClient;
  readonly pending: readonly string[];
}

/**
 * Turns the raw event log into what a person reads: chat messages, clusters of
 * tool activity (a call joined to its result), decision cards and notices.
 * Cursor streams one reply as several agent messages, so those are joined.
 */
export function buildTranscript(
  records: readonly { sequence: number; event: NyxEvent }[],
  input: TranscriptInput,
): TranscriptItem[] {
  const decisions = new Map<string, boolean | null>();
  for (const { event } of records)
    if (event.kind === 'answered') decisions.set(event.requestId, event.approved);
  const pending = new Set(input.pending);
  const items: TranscriptItem[] = [];
  const openCalls = new Map<string, ToolRow>();
  let previousWasMessage = false;
  for (const { sequence, event } of records) {
    const last = items.at(-1);
    switch (event.kind) {
      case 'message': {
        if (
          input.client === 'cursor' &&
          previousWasMessage &&
          event.speaker === 'agent' &&
          last?.kind === 'message' &&
          last.speaker === 'agent'
        )
          last.text += event.text;
        else
          items.push({ kind: 'message', key: sequence, speaker: event.speaker, text: event.text });
        break;
      }
      case 'tool_call': {
        const row: ToolRow = {
          key: sequence,
          name: event.name,
          detail: event.detail,
          result: null,
          status: 'pending',
        };
        if (event.callId) openCalls.set(event.callId, row);
        if (last?.kind === 'activity') last.tools.push(row);
        else items.push({ kind: 'activity', key: sequence, tools: [row] });
        break;
      }
      case 'tool_result': {
        const open = event.callId ? openCalls.get(event.callId) : undefined;
        if (open) {
          openCalls.delete(event.callId!);
          replaceTool(items, open, {
            ...open,
            result: event.detail,
            status: event.failed ? 'failed' : 'done',
          });
        } else {
          const row: ToolRow = {
            key: sequence,
            name: event.name,
            detail: '',
            result: event.detail,
            status: event.failed ? 'failed' : 'done',
          };
          if (last?.kind === 'activity') last.tools.push(row);
          else items.push({ kind: 'activity', key: sequence, tools: [row] });
        }
        break;
      }
      case 'approval':
        items.push({
          kind: 'approval',
          key: sequence,
          requestId: event.requestId,
          action: event.action,
          summary: event.summary,
          pending: pending.has(event.requestId),
          decision: decisions.get(event.requestId) ?? null,
        });
        break;
      case 'question':
        items.push({
          kind: 'question',
          key: sequence,
          requestId: event.requestId,
          questions: event.questions,
          pending: pending.has(event.requestId),
          answered: decisions.has(event.requestId),
        });
        break;
      case 'turn_finished':
        if (event.error)
          items.push({ kind: 'notice', key: sequence, tone: 'danger', text: event.error });
        break;
      case 'rate_limit':
        items.push({ kind: 'notice', key: sequence, tone: 'warn', text: event.detail });
        break;
      case 'failed':
        items.push({
          kind: 'notice',
          key: sequence,
          tone: 'danger',
          text: `The task failed (${event.code}).`,
        });
        break;
      case 'turn_started':
      case 'answered':
        break;
    }
    previousWasMessage = event.kind === 'message';
  }
  return items;
}

function replaceTool(items: TranscriptItem[], previous: ToolRow, next: ToolRow) {
  for (const item of items) {
    if (item.kind !== 'activity') continue;
    const index = item.tools.indexOf(previous);
    if (index >= 0) {
      item.tools[index] = next;
      return;
    }
  }
}

/** A short line for a tool cluster: "3 tool calls · 1 failed". */
export function activitySummary(tools: readonly ToolRow[]): string {
  const failed = tools.filter((tool) => tool.status === 'failed').length;
  const pending = tools.filter((tool) => tool.status === 'pending').length;
  const count = tools.length === 1 ? tools[0]!.name : `${tools.length} tool calls`;
  const notes = [failed ? `${failed} failed` : '', pending ? `${pending} in progress` : ''].filter(
    Boolean,
  );
  return notes.length ? `${count} · ${notes.join(' · ')}` : count;
}

/** Textarea rows that fit the draft without a measuring pass or inline style. */
export function composerRows(text: string, min = 1, max = 8): number {
  return Math.min(max, Math.max(min, text.split('\n').length));
}

/** The composer explains why it is idle rather than silently disabling itself. */
export function composerHint(task: NyxTask | null, canControl: boolean): string {
  if (!canControl) return 'Read access only. Send messages and decisions from Nyx Link.';
  if (!task) return '';
  switch (task.state) {
    case 'running':
      return 'Working… press Stop to interrupt, then send.';
    case 'waiting':
      return 'Answer the pending request above to continue.';
    case 'ended':
    case 'interrupted':
    case 'failed':
      return 'This task has ended. Start a new task to continue.';
    case 'idle':
      return 'Enter to send · Shift+Enter for a new line';
  }
}
